// @import

import { ensureHTMLElement } from "@src/js/lib/ensure";
import AsyncListManager, { Filters } from "@src/js/lib/listing/list-manager";
import { addPaginationHandler } from "@src/js/lib/listing/pagination";
import { XHRResponse } from "@src/js/lib/request";
import Alpine from "alpinejs";

Alpine.directive("listing", (element, { expression }, { cleanup }) => {
  const postType = expression;
  const LOAD_MORE_VISIBLE_CLASS = "c-listing__load-more-btn--visible";

  const dom = {
    listCards: ensureHTMLElement(element.querySelector(".c-listing__cards")),
    pagination: element.querySelector<HTMLElement>(".c-listing__pagination"),
    dropDownOptions: element.querySelectorAll(".c-dropdown__option"),
    loadMore: element.querySelector(".js-load-more"),
    searchBtn: element.querySelector(".js-btn-search"),
    inputSearch: element.querySelector<HTMLInputElement>(".js-input-search"),
    form: element.querySelector("form"),
    mobileFiltersButtons: element.querySelectorAll<HTMLElement>(
      ".c-filter-bar__filters-mobile-category-options .c-button",
    ),
    mobileFiltersApply: element.querySelector(".c-filter-bar__mobile-filters-btn-apply"),
    mobileFiltersTrigger: element.querySelector(".c-filter-bar__mobile-filters-trigger"),
    mobileFiltersOverlay: element.querySelector(".c-filter-bar__mobile-filters-overlay"),
    mobileFiltersDrawer: element.querySelector(".c-filter-bar__mobile-filters-drawer"),
    mobileFiltersClose: element.querySelector(".c-filter-bar__mobile-filters-close"),
  };

  type FilterBy = `${string}=${string}`;
  const query = (element.dataset.query ? element.dataset.query.split("&") : []) as FilterBy[];
  const isNumeredPagination = !!dom.pagination;

  function queryToFilters(part: FilterBy) {
    const [name, value] = part.split("=");
    if (name === "s" || name === "pa") return [null, null]; // Don't treat search and page query params as filters
    const type = name === "campus" ? "meta" : "taxo";
    const values = { value, name, type };
    return [name, values];
  }

  function queryToPage(part: FilterBy) {
    const [name, value] = part.split("=");
    if (name === "pa") return Number(value);
    return null;
  }

  const initialFilters = Object.fromEntries(query.map(queryToFilters).filter((item) => !!item[0]));
  const initialPage = query.map(queryToPage).find(Boolean) ?? 1;

  const listManager = new AsyncListManager({
    action: "sp_theme_posts_load_ajax",
    options: {
      isSearch: true,
      type: postType,
      page: initialPage,
      filters: (element.dataset.query ? initialFilters : {}) as Filters,
    },
    onLoadSuccess: injectData,
  });

  // Util to parse filter attributes from a clicked element
  function parseFilterAttributes(target: HTMLElement) {
    const query = target.getAttribute("href")
      ? target.getAttribute("href")?.split("?")[1]
      : target.dataset.query;

    const [slug, value] = query ? query.split("=") : [null, null];
    if (!slug || !value) {
      console.warn("Missing or invalid href query parameters on filter option");
      return null;
    }
    const type = slug === "campus" ? "meta" : "taxo";
    return { slug, value, type };
  }

  function showEmptyResult() {
    element.classList.add("no-results");
  }

  function hideEmptyResult() {
    element.classList.remove("no-results");
  }

  // Load More button click handler
  function handleLoadMoreClick(e: Event) {
    e.preventDefault();
    listManager.loadMore();
  }

  // Submit search
  function triggerSearch() {
    if (typeof dom.inputSearch?.value !== "string") return;
    listManager.setOption("searchValue", dom.inputSearch.value);
    // A new search must always restart pagination from the first page.
    listManager.setOption("page", 1);
    listManager.load();
  }

  // Dropdown button click handler
  function handleFilterClick(event: Event) {
    event.preventDefault();
    const { slug, value, type } = parseFilterAttributes(event.currentTarget as HTMLElement) || {};
    if (!slug || !value || !type) return;

    listManager.setFilter(slug, { value, name: slug, type });
    // Any filter change invalidates the current page window.
    listManager.setOption("page", 1);
    listManager.load();
  }

  /**
   *  Mobile filters
   */

  // Show mobile panel
  function showFiltersMobile() {
    const mobileFiltersContainer = element.querySelector(".c-filter-bar__filters-mobile");
    if (!mobileFiltersContainer) return;

    mobileFiltersContainer.classList.toggle("c-filter-bar__filters-mobile--open");
  }

  // Hide mobile panel
  function hideFiltersMobile() {
    const mobileFiltersContainer = element.querySelector(".c-filter-bar__filters-mobile");
    if (!mobileFiltersContainer) return;

    mobileFiltersContainer.classList.toggle("c-filter-bar__filters-mobile--open");
  }

  function handleFiltersMobileClick(event: Event) {
    event.preventDefault();
    const target = event.currentTarget as HTMLElement;
    const { slug, value, type } = parseFilterAttributes(target) || {};
    if (!slug || !value || !type) return;

    const isActive = target.hasAttribute("data-active");
    if (isActive) return;

    // Set all buttons inactive
    dom.mobileFiltersButtons.forEach((btn) => {
      if (
        (btn.dataset.query && btn.dataset.query.includes(slug)) ||
        (btn instanceof HTMLAnchorElement && btn.getAttribute("href")?.includes(slug))
      ) {
        btn.removeAttribute("data-active");
      }
    });

    target.setAttribute("data-active", "true");
    listManager.setFilter(slug, { value, name: slug, type });
  }

  function applyMobileFilters() {
    // Apply mobile filters from page 1 to avoid stale page indexes.
    listManager.setOption("page", 1);
    listManager.load();
    hideFiltersMobile();
  }

  // Replace or add content to the listing
  function injectData(response: XHRResponse) {
    if (response.data.has_results == false) {
      showEmptyResult();
      dom.loadMore?.classList.remove(LOAD_MORE_VISIBLE_CLASS);
    } else {
      hideEmptyResult();

      //manage load more
      if (!isNumeredPagination) {
        const showHide = ["remove", "add"][Number(response.data.load_more)];
        dom.loadMore?.classList[showHide](LOAD_MORE_VISIBLE_CLASS);
      }

      // Insert dom
      // For search, filters, numererd pagination we replace the content
      if (response.data.page == 1 || isNumeredPagination) {
        dom.listCards.innerHTML = response.data.posts_html;
        scrollTo({ top: element.getBoundingClientRect().top + window.scrollY - 100, behavior: "smooth" });
      } else {
        // For load more we append the new results
        dom.listCards.insertAdjacentHTML("beforeend", response.data.posts_html);
      }
    }
  }

  function bind() {
    function apply(method: "addEventListener" | "removeEventListener") {
      dom.dropDownOptions.forEach((option) => option[method]("click", handleFilterClick));
      dom.mobileFiltersButtons.forEach((option) => option[method]("click", handleFiltersMobileClick));

      // Buttons
      dom.loadMore?.[method]("click", handleLoadMoreClick);
      dom.mobileFiltersTrigger?.[method]("click", showFiltersMobile);
      dom.mobileFiltersOverlay?.[method]("click", hideFiltersMobile);
      dom.mobileFiltersClose?.[method]("click", hideFiltersMobile);
      dom.mobileFiltersApply?.[method]("click", applyMobileFilters);

      // Search
      if (dom.inputSearch) {
        dom.searchBtn?.[method]("click", triggerSearch);
      }

      dom.form?.[method]("submit", (e: Event) => {
        e.preventDefault();
        triggerSearch();
      });
    }

    // Activate
    apply("addEventListener");
    // Return cleanup
    return () => apply("removeEventListener");
  }

  const unbind = bind();
  const removePaginationHandlers = addPaginationHandler(dom.pagination, listManager);

  cleanup(() => {
    unbind();
    removePaginationHandlers();
  });
});
